import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { App, ConfiguredApp } from '../../../interfaces/app';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppsService } from '../../../services/apps/apps.service';
import { distinctUntilChanged, filter, map, pluck } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { AdminService } from '../../../services/admin/admin.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { isEqual } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { AddMediasForAppComponent } from '../../../components/modals/add-medias-for-app/add-medias-for-app.component';
import { IPreparedFile } from '../../../services/file-upload.service';
import { ConfirmationDialogComponent } from '../../../components/modals/confirm-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';

type RestaurantSelectionMode = 'all' | 'custom';

type AppRestaurantsForm = {
  mode: FormControl<RestaurantSelectionMode>;
  toAll: FormControl<boolean>;
  ids: FormControl<number[]>;
};

type AppConfigurationForm = {
  name: FormControl<string>;
  description: FormControl<string>;
  restaurants: FormGroup<AppRestaurantsForm>;
  files: FormControl<number[]>;
  published: FormControl<boolean>;
  config: FormControl<unknown>;
};

type AppConfigurationFormValue = ReturnType<FormGroup<AppConfigurationForm>['getRawValue']>;

@UntilDestroy()
@Component({
  selector: 'app-edit-app-page',
  templateUrl: './app-configured-page.component.html',
  styleUrls: ['./app-configured-page.component.scss'],
  animations: [
    trigger('slideFormField', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class AppConfiguredPageComponent implements OnInit {

  public chainId!: number;
  public appId!: string;
  public configuredId!: string;
  public isLoadingSubject = new BehaviorSubject(false);
  public allRestaurants$!: Observable<IRestaurantInfo[]>;
  private normalizedOriginalValue!: AppConfigurationFormValue;
  private appConfiguredSubject = new BehaviorSubject<ConfiguredApp | null>(null);
  public files: IPreparedFile[]  = [];
  public app!: App;

  form = new FormGroup<AppConfigurationForm>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
    description: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    restaurants: new FormGroup<AppRestaurantsForm>({
      mode: new FormControl('all', { nonNullable: true }),
      toAll: new FormControl(true, { nonNullable: true }),
      ids: new FormControl<number[]>([], { nonNullable: true }),
    }),
    files: new FormControl<number[]>([], { nonNullable: true }),
    published: new FormControl(false, { nonNullable: true }),
    config: new FormControl<unknown>({}, { nonNullable: true })
  });

  private formValue$ = new BehaviorSubject<AppConfigurationFormValue>(this.form.getRawValue());
  public hasChanges$ = this.formValue$.pipe(
    map(value => !isEqual(value, this.normalizedOriginalValue)),
    distinctUntilChanged()
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apps: AppsService,
    private readonly adminService: AdminService,
    private readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly activateRoute: ActivatedRoute,
  ) {
    this.activateRoute.data.pipe(
      pluck('app'),
      untilDestroyed(this),
    ).subscribe((app: App) => {
      this.app = app;
    });
  }

  ngOnInit(): void {
    this.chainId = Number(this.route.parent?.snapshot.paramMap.get('id'));
    this.appId = this.route.snapshot.paramMap.get('appId') || '';
    this.configuredId = this.route.snapshot.paramMap.get('configuredId') || '';
    this.getAllRestaurants();

    this.form.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        untilDestroyed(this)
      )
      .subscribe(() => this.formValue$.next(this.form.getRawValue()));

    const { mode: modeControl, ids: idsControl } = this.restaurantsFormGroup.controls;

    modeControl.valueChanges.pipe(
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((mode): void => {
      if (mode === 'custom') {
        idsControl.setValidators([Validators.required]);
      } else {
        idsControl.clearValidators();
        idsControl.setValue([]);
      }
      idsControl.updateValueAndValidity();
    });

    this.loadAppData();
  }

  get restaurantsFormGroup(): FormGroup<AppRestaurantsForm> {
    return this.form.controls.restaurants;
  }

  private loadAppData(): void {
    this.isLoadingSubject.next(true);
    this.apps.getConfiguredAppById(this.chainId, this.configuredId)
    .pipe(
      untilDestroyed(this)
    ).subscribe({
      next: (configuredApp) => {
        this.patchForm(configuredApp);
        this.appConfiguredSubject.next(configuredApp);
        this.normalizedOriginalValue = this.form.getRawValue();
        this.formValue$.next(this.form.getRawValue());
        this.files = configuredApp.files?.map(file => ({
          id: file.id,
          url: file.url,
          name: file.name
        })) || [];
        this.isLoadingSubject.next(false);
      },
      error: () => {
        this.isLoadingSubject.next(false);
      }
    });
  }

  private patchForm(app: ConfiguredApp): void {
    const restaurantsValue = app.restaurants;
    const isAllRestaurants = restaurantsValue.all;
    const restaurantIds = restaurantsValue.ids || [];

    this.form.patchValue({
      name: app.name,
      description: app.description || '',
      published: app.published || false,
      files: app.files?.map(file => file.id) || [],
      config: app.config || {}
    });

    this.restaurantsFormGroup.patchValue({
      mode: isAllRestaurants ? 'all' : 'custom',
      ids: restaurantIds
    });
  }

  public getAllRestaurants(): void {
    this.allRestaurants$ = this.adminService.getRestaurantsByChainId(this.chainId);
  }

  get isLoading(): boolean {
    return this.isLoadingSubject.value;
  }

  public update(): void {
    this.isLoadingSubject.next(true);

    const formValue = this.form.getRawValue();
    const restaurantsValue = formValue.restaurants;

    this.apps.updateAppConfiguration(this.chainId, this.configuredId, {
      appId: this.appId,
      name: formValue.name,
      description: formValue.description,
      published: formValue.published,
      restaurants: restaurantsValue.mode === 'all' ?
        { all: true, ids: [] } :
        { all: false, ids: restaurantsValue.ids || [] },
      files: formValue.files,
      config: formValue.config,
    }).pipe(
      untilDestroyed(this)
    ).subscribe({
      next: () => {
        this.isLoadingSubject.next(false);
      },
      error: () => {
        this.isLoadingSubject.next(false);
      }
    });
  }

  public deleteAppConfiguration(): void {
    const title = this.translate.instant('pages.apps.messages.deleteAppTitle');
    const message = this.translate.instant('pages.apps.messages.deleteAppConfirm');

    const confirmationModal = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        message,
        title
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((): void => {
      this.apps.deleteAppConfiguration(this.chainId, this.configuredId).pipe(
        untilDestroyed(this)
      ).subscribe(() => {
          this.router.navigate(['/chains', this.chainId.toString(), 'apps', 'app', this.appId]);
        }
      );
    });
  }

  public backToAppsList(): void {
    this.router.navigate(['/chains', this.chainId.toString(), 'apps', 'app', this.appId]);
  }

  public addMedia(): void {
    const mediaModal = this.matDialog.open(AddMediasForAppComponent, {
      data: {
        files: this.files
      }
    });

    mediaModal.afterClosed().pipe(
      untilDestroyed(this)
    ).subscribe((result) => {
      if (result?.files) {
        this.files = result?.files;

        const filesControl = this.form.get('files');
        if (filesControl) {
          filesControl.setValue(this.files.map((file) => file.id));
        }
      }
    });
  }
}
